import { useEffect, useState, useMemo } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, Panel, Pill, StatCard, EmptyState } from "@/components/Bits";
import { toast } from "sonner";
import {
  Clock, CheckCircle, Warning, ClipboardText, MapPin, Camera,
  SignIn, SignOut as SignOutIcon, Plus, Funnel, Download, Eye, FileText,
} from "@phosphor-icons/react";
import PayslipDetailDrawer from "@/components/PayslipDetailDrawer";

export default function StaffDashboard() {
  const [profile, setProfile] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [leaveBal, setLeaveBal] = useState(null);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [showIncident, setShowIncident] = useState(false);
  const [showLeave, setShowLeave] = useState(false);

  const load = async () => {
    const [p, t, a, l, ps, lb] = await Promise.all([
      api.get("/hrms/me/profile"),
      api.get("/hrms/me/tasks"),
      api.get("/hrms/me/attendance"),
      api.get("/hrms/me/leaves"),
      // Self-service: works for ANY staff (teacher / non-teaching / admin
      // who is also an employee). Auto-scopes to own staff_id.
      api.get("/payroll/me/payslips").catch(() => ({ data: [] })),
      api.get("/payroll/me/leave-balance").catch(() => ({ data: null })),
    ]);
    setProfile(p.data);
    setTasks(t.data);
    setAttendance(a.data);
    setLeaves(l.data);
    setPayslips(ps.data || []);
    setLeaveBal(lb.data);
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayAtt = useMemo(() => attendance.find(a => a.date === today), [attendance, today]);

  const clockIn = async () => {
    await api.post("/hrms/me/clock-in", {});
    toast.success("Clocked in 🌅");
    load();
  };
  const clockOut = async () => {
    await api.post("/hrms/me/clock-out", {});
    toast.success("Clocked out 🌙");
    load();
  };

  const completeTask = async (taskId, items, total) => {
    await api.post(`/hrms/tasks/${taskId}/complete`, {
      completed_items: Array.from({ length: total }, (_, i) => i),
      note: "Completed via my-tasks dashboard",
    });
    toast.success("Task completed ✓");
    load();
  };

  return (
    <div data-testid="staff-dashboard" className="max-w-6xl">
      <PageHeader
        eyebrow={profile?.designation || "My Workspace"}
        title={`Hello, ${profile?.name?.split(" ")[0] || "Friend"} 🌿`}
        subtitle="Your daily checklist, attendance, leave & quick incident reports."
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-3xl border border-border p-5 sm:col-span-2">
          <div className="text-xs text-ink-soft uppercase tracking-wider">Today · {today}</div>
          <div className="mt-3 flex items-center gap-3">
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${todayAtt ? "bg-sage-soft text-sage-deep" : "bg-mustard-soft text-mustard-hover"}`}>
              {todayAtt ? <CheckCircle size={24} weight="duotone" /> : <Clock size={24} weight="duotone" />}
            </span>
            <div className="flex-1">
              <div className="font-heading font-bold text-lg">
                {todayAtt ? `Clocked in at ${todayAtt.arrival_time || "—"}` : "Not clocked in yet"}
              </div>
              {todayAtt?.departure_time && <div className="text-xs text-ink-soft mt-0.5">Clocked out at {todayAtt.departure_time}</div>}
            </div>
            {!todayAtt ? (
              <button data-testid="clock-in-btn" onClick={clockIn} className="px-5 py-2.5 rounded-full bg-sage text-white font-semibold flex items-center gap-2">
                <SignIn weight="bold" /> Clock In
              </button>
            ) : !todayAtt.departure_time ? (
              <button data-testid="clock-out-btn" onClick={clockOut} className="px-5 py-2.5 rounded-full bg-terracotta text-white font-semibold flex items-center gap-2">
                <SignOutIcon weight="bold" /> Clock Out
              </button>
            ) : <Pill tone="sage">Done for today</Pill>}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-border p-5">
          <div className="text-xs text-ink-soft uppercase tracking-wider">Quick Actions</div>
          <div className="mt-3 space-y-2">
            <button data-testid="report-incident-btn" onClick={() => setShowIncident(true)} className="w-full px-4 py-2.5 rounded-2xl bg-red-50 text-red-700 font-medium text-sm flex items-center gap-2">
              <Warning weight="duotone" /> Report Incident
            </button>
            <button data-testid="apply-leave-btn" onClick={() => setShowLeave(true)} className="w-full px-4 py-2.5 rounded-2xl bg-mustard-soft text-mustard-hover font-medium text-sm flex items-center gap-2">
              <ClipboardText weight="duotone" /> Apply Leave
            </button>
          </div>
        </div>
      </div>

      <Panel title="My Tasks Today" testid="my-tasks">
        {tasks.length === 0 ? (
          <EmptyState title="No tasks assigned" hint="Your supervisor hasn't assigned any tasks yet." />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {tasks.map(t => {
              const total = (t.checklist || []).length;
              const done = (t.completed_items || []).length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <div key={t.task_id} className="border border-border rounded-2xl p-4" data-testid={`task-${t.task_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{t.title}</div>
                      <div className="text-xs text-ink-soft mt-0.5">{t.description}</div>
                    </div>
                    <Pill tone={t.status === "done" ? "sage" : "mustard"}>{t.status}</Pill>
                  </div>
                  {total > 0 && (
                    <>
                      <div className="mt-3 space-y-1">
                        {(t.checklist || []).map((item, idx) => {
                          const completed = (t.completed_items || []).includes(idx);
                          return (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <span className={`w-4 h-4 rounded-full flex items-center justify-center ${completed ? "bg-sage text-white" : "border-2 border-border"}`}>
                                {completed && <CheckCircle size={10} weight="fill" />}
                              </span>
                              <span className={completed ? "line-through text-ink-soft" : ""}>{item}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex-1 h-1.5 bg-paper-alt rounded-full overflow-hidden">
                          <div className="h-full bg-sage" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-ink-soft">{pct}%</span>
                      </div>
                      {t.status !== "done" && (
                        <button onClick={() => completeTask(t.task_id, [], total)} data-testid={`complete-task-${t.task_id}`} className="mt-3 w-full px-3 py-2 rounded-full bg-terracotta text-white text-xs font-semibold">
                          Mark all complete
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <Panel title="My Attendance" testid="my-attendance">
          {attendance.length === 0 ? <EmptyState title="No records" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-ink-soft text-xs uppercase tracking-wider text-left"><th className="py-2">Date</th><th>In</th><th>Out</th><th>Status</th></tr></thead>
              <tbody>
                {attendance.slice(0, 10).map(a => (
                  <tr key={a.attendance_id} className="border-t border-border">
                    <td className="py-2">{a.date}</td>
                    <td className="py-2 text-xs">{a.arrival_time || "—"}</td>
                    <td className="py-2 text-xs">{a.departure_time || "—"}</td>
                    <td className="py-2"><Pill tone={a.status === "present" ? "sage" : "mustard"}>{a.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="My Leaves" testid="my-leaves">
          {leaves.length === 0 ? <EmptyState title="No leave applied" /> : (
            <div className="space-y-2">
              {leaves.map(l => (
                <div key={l.leave_id} className="border border-border rounded-2xl p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm capitalize">{l.kind} leave</div>
                    <div className="text-xs text-ink-soft">{l.start_date} → {l.end_date}</div>
                    <div className="text-xs text-ink-soft mt-1">{l.reason}</div>
                  </div>
                  <Pill tone={l.status === "approved" ? "sage" : l.status === "rejected" ? "danger" : "mustard"}>{l.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="My Payslips" testid="my-payslips" className="mt-6">
        {payslips.length === 0 ? (
          <EmptyState title="No payslips yet" hint="Payslips appear here after each monthly payroll run is finalized." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-ink-soft text-xs uppercase tracking-wider text-left">
                  <th className="py-2">Period</th>
                  <th>Days (W/P)</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Deductions</th>
                  <th className="text-right">Net payable</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payslips.slice(0, 12).map((p) => (
                  <tr key={p.payslip_id} className="border-t border-border hover:bg-paper-alt" data-testid={`payslip-${p.payslip_id}`}>
                    <td className="py-3 font-medium">{prettyPeriod(p.period)}</td>
                    <td className="text-xs">{p.working_days} / <span className="text-sage-deep">{p.present_days}</span></td>
                    <td className="text-right">{(p.currency || "INR") === "INR" ? `₹${(p.gross || 0).toLocaleString()}` : `${p.currency} ${(p.gross || 0).toLocaleString()}`}</td>
                    <td className="text-right text-terracotta">−{(p.currency || "INR") === "INR" ? `₹${(p.deductions_total || 0).toLocaleString()}` : `${p.currency} ${(p.deductions_total || 0).toLocaleString()}`}</td>
                    <td className="text-right font-semibold text-sage-deep">{(p.currency || "INR") === "INR" ? `₹${(p.net_payable || 0).toLocaleString()}` : `${p.currency} ${(p.net_payable || 0).toLocaleString()}`}</td>
                    <td><Pill tone={p.status === "finalized" ? "sage" : "mustard"}>{p.status}</Pill></td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSelectedSlip(p)}
                          className="text-xs px-3 py-1.5 rounded-full border border-border inline-flex items-center gap-1 hover:bg-white"
                          data-testid={`payslip-view-${p.payslip_id}`}
                        >
                          <Eye size={12} weight="bold" /> View
                        </button>
                        <a
                          href={`${API_BASE}/payroll/payslips/${p.payslip_id}/pdf`}
                          target="_blank" rel="noreferrer"
                          className="text-xs px-3 py-1.5 rounded-full border border-border inline-flex items-center gap-1 hover:bg-white"
                          data-testid={`payslip-pdf-${p.payslip_id}`}
                        >
                          <FileText size={12} weight="bold" /> PDF
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {leaveBal && (
        <Panel title="My leave balance" testid="my-leave-balance" className="mt-6">
          <div className="grid grid-cols-5 gap-3">
            {["sick", "casual", "earned", "compensatory", "unpaid"].map(k => (
              <div key={k} className="bg-white border border-border rounded-2xl py-3 px-3 text-center">
                <div className="text-2xl font-bold text-ink">{leaveBal[k] ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink-soft capitalize mt-0.5">{k === "unpaid" ? "Unpaid taken" : k}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {selectedSlip && (
        <PayslipDetailDrawer payslip={selectedSlip} onClose={() => setSelectedSlip(null)} />
      )}

      {showIncident && <IncidentDialog onClose={() => setShowIncident(false)} onCreated={load} />}
      {showLeave && <LeaveDialog onClose={() => setShowLeave(false)} onCreated={load} />}
    </div>
  );
}

function prettyPeriod(p) {
  if (!p) return "—";
  try {
    const [y, m] = p.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  } catch { return p; }
}

function IncidentDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("low");
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!title || !description) return toast.error("Title & description required");
    setSubmitting(true);
    try {
      let geo = null;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
        geo = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy };
      } catch {}
      await api.post("/hrms/incidents", { title, description, severity, location, geo });
      toast.success("Incident reported. Admin notified.");
      onClose();
      onCreated();
    } catch (e) {
      toast.error("Failed to report");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="incident-dialog">
        <div className="font-heading font-bold text-xl">Report an Incident</div>
        <p className="text-xs text-ink-soft mt-1">Help us keep MangoCubs safe. Your location is captured automatically.</p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-ink-soft font-medium">Title</label>
            <input data-testid="incident-title" value={title} onChange={e => setTitle(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" placeholder="Short title" />
          </div>
          <div>
            <label className="text-xs text-ink-soft font-medium">Description</label>
            <textarea data-testid="incident-desc" value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 rounded-xl bg-paper-alt border border-border" rows={4} placeholder="What happened?" />
          </div>
          <div>
            <label className="text-xs text-ink-soft font-medium">Location</label>
            <input data-testid="incident-loc" value={location} onChange={e => setLocation(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" placeholder="E.g. Classroom A1" />
          </div>
          <div>
            <label className="text-xs text-ink-soft font-medium">Severity</label>
            <div className="flex gap-2 mt-1">
              {["low", "medium", "high"].map(s => (
                <button key={s} data-testid={`severity-${s}`} onClick={() => setSeverity(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${severity === s ? (s === "high" ? "bg-red-500 text-white" : s === "medium" ? "bg-mustard text-white" : "bg-sage text-white") : "bg-white border border-border"}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium text-ink-soft">Cancel</button>
          <button data-testid="incident-submit" disabled={submitting} onClick={submit} className="px-4 py-2 rounded-full bg-terracotta text-white text-sm font-semibold disabled:opacity-50">{submitting ? "Submitting…" : "Submit"}</button>
        </div>
      </div>
    </div>
  );
}

function LeaveDialog({ onClose, onCreated }) {
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [kind, setKind] = useState("casual");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) return toast.error("Reason required");
    setSubmitting(true);
    try {
      await api.post("/hrms/leaves", { start_date: start, end_date: end, kind, reason });
      toast.success("Leave requested. Awaiting approval.");
      onClose();
      onCreated();
    } catch {
      toast.error("Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="leave-dialog">
        <div className="font-heading font-bold text-xl">Apply for Leave</div>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-ink-soft font-medium">From</label>
              <input data-testid="leave-start" type="date" value={start} onChange={e => setStart(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
            </div>
            <div>
              <label className="text-xs text-ink-soft font-medium">To</label>
              <input data-testid="leave-end" type="date" value={end} onChange={e => setEnd(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
            </div>
          </div>
          <div>
            <label className="text-xs text-ink-soft font-medium">Type</label>
            <div className="flex gap-2 mt-1">
              {["sick", "casual", "earned", "unpaid"].map(k => (
                <button key={k} data-testid={`leave-kind-${k}`} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${kind === k ? "bg-terracotta text-white" : "bg-white border border-border"}`}>{k}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-ink-soft font-medium">Reason</label>
            <textarea data-testid="leave-reason" value={reason} onChange={e => setReason(e.target.value)} className="w-full px-3 py-2 rounded-xl bg-paper-alt border border-border" rows={3} />
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium text-ink-soft">Cancel</button>
          <button data-testid="leave-submit" disabled={submitting} onClick={submit} className="px-4 py-2 rounded-full bg-terracotta text-white text-sm font-semibold disabled:opacity-50">{submitting ? "Submitting…" : "Submit"}</button>
        </div>
      </div>
    </div>
  );
}
